import React, { useState } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { Layout as AntLayout, theme } from 'antd'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { toggleCollapsed } from '@/store/slices/userSlice'
import { findMenuById, generateMenuItems } from '@/utils/menu'
import Header from './Header'
import Sidebar from './Sidebar'
import MobileMask from './MobileMask'
import useMenuState from './hooks/useMenuState'
import useMobile from './hooks/useMobile'
import useMenuAccordion from './hooks/useMenuAccordion'
import './index.less'

const { Content } = AntLayout

const Layout: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { collapsed, theme: currentTheme, menus } = useAppSelector((state) => state.user)
  
  const isMobile = useMobile()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { selectedKeys, setSelectedKeys, openKeys, setOpenKeys } = useMenuState()
  
  // 生成菜单项
  const menuItems = generateMenuItems(menus, navigate)
  
  // 使用 Accordion 模式的菜单展开处理
  const createAccordionHandler = useMenuAccordion(menuItems)

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken()

  // 如果是登录页面，不显示侧边栏
  const isLoginPage = location.pathname === '/login'

  // 处理菜单点击
  const handleMenuClick = ({ key }: { key: string }) => {
    const menu = findMenuById(menus, key)
    if (menu && menu.path) {
      navigate(menu.path)
      // 移动端点击菜单项后关闭菜单
      if (isMobile) {
        setMobileMenuOpen(false)
      }
    }
  }

  // 处理菜单展开/收起（使用 Accordion 模式）
  const onOpenKeysChange = (keys: string[]) => {
    const handler = createAccordionHandler(openKeys)
    const newKeys = handler(keys)
    setOpenKeys(newKeys)
  }

  // 如果是登录页面，只显示内容区域
  if (isLoginPage) {
    return (
      <div style={{ height: '100vh', width: '100vw' }}>
        <Outlet />
      </div>
    )
  }

  return (
    <AntLayout className={`admin-layout ${collapsed ? 'collapsed' : ''}`}>
      {/* 移动端遮罩层 */}
      <MobileMask visible={isMobile && mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
      
      <Sidebar
        collapsed={collapsed}
        currentTheme={currentTheme}
        selectedKeys={selectedKeys}
        openKeys={openKeys}
        onOpenKeysChange={onOpenKeysChange}
        onMenuClick={handleMenuClick}
        isMobile={isMobile}
        mobileMenuOpen={mobileMenuOpen}
      />
      
      <AntLayout>
        <Header
          collapsed={collapsed}
          currentTheme={currentTheme}
          onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)}
          isMobile={isMobile}
          style={{ background: colorBgContainer }}
        />
        
        <Content className="admin-content">
          <div
            className="admin-content-wrapper"
            style={{
              padding: 24,
              minHeight: 280,
              background: colorBgContainer,
              borderRadius: borderRadiusLG,
            }}
          >
            <Outlet />
          </div>
        </Content>
      </AntLayout>
    </AntLayout>
  )
}

export default Layout
